/**
 * chatDocumentWait.js — Lane 690, "the chat turn waits for the letter".
 *
 * RYAN'S DEFECT, verbatim (QA-1 finding 4): he attached a supplier letter,
 * typed a question, pressed Send once — and the answer was composed *while the
 * document was still being read*. It was thin. He then typed "did you read the
 * notice I uploaded?" and got the answer he wanted: what the notice said, its
 * effective date, the raw-material justification assessed on its merits, the
 * 75% on-time record used as a counter-angle, and an instruction to make them
 * quantify the increase. **That second answer is what the first one should have
 * been.**
 *
 * `Home.js handleChatSubmit` fired the document reads and the chat turn in the
 * same tick and awaited neither against the other. Its own comment named this
 * lane as the one that would fix it. This module is the decision half of that
 * fix: everything about *when a chat turn may stop waiting for an attached
 * document* lives here, pure and unit-tested, so the rule can be red-tested
 * without mounting a page. `docUploadStatus.js` and `noticeAttachStatus.js` are
 * the house precedent for putting the judgement in a pure module and leaving
 * the component to render it.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THE WAIT MUST INCLUDE THE HUMAN CONFIRM, WHICH IS NOT OBVIOUS
 * ─────────────────────────────────────────────────────────────────────────
 * The tempting shape is "await the upload request, then send". It would be
 * simpler, it would involve no human, and **it would deliver nothing at all.**
 *
 * A supplier commercial notice is read, verified and then PARKED at
 * `awaiting_supplier` having written **zero** assertions
 * (`backend/routes/doc_upload.py`; the card comment at `Home.js` says the same).
 * The claims become readable only when the buyer answers "who sent this?" —
 * `helpers/notice_write.attach_supplier` is the first and only writer, and
 * `helpers/notice_conv_anchor.establish_notice_subject` sets the conversation's
 * supplier subject on the same call. Chat reads those claims through
 * `fetch_notice_claims`, off the `assertions` table, and
 * `get_notice_claims_context_for_ai` returns `""` unless the turn is
 * supplier-scoped — which, for a freshly uploaded letter, is precisely what the
 * confirm establishes.
 *
 * So: wait for extraction only, and Section E.5 is empty and the scope is
 * unset. The turn would be slower and no better — the worst of both. The wait
 * therefore spans BOTH beats. That is a deliberate decision, not an oversight,
 * and it is the decision this module encodes.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ...AND WHY A HUMAN INSIDE A CHAT TURN IS NEVERTHELESS SAFE HERE
 * ─────────────────────────────────────────────────────────────────────────
 * A wait on a human click is a hang waiting to happen, and the composer is
 * genuinely inert while a turn is in flight (`Home.js` disables both the input
 * and Send on `chatLoading`). Four independent mitigations each break the hang
 * on their own, so no single one is load-bearing:
 *
 *   1. SCOPED TO THIS SUBMIT. Only ids attached to *this* Send are waited on.
 *      A card left unconfirmed on an earlier turn can never block a later one.
 *   2. SAID OUT LOUD, WHERE THE ANSWER WOULD BE. The waiting line names the
 *      file and what is being waited for, and renders in the transcript
 *      directly above the confirm card. There is no unnoticed modal.
 *   3. ESCAPABLE IN ONE CLICK. `Answer now` is rendered for the whole wait —
 *      including the reading phase, and never gated on the picker being up —
 *      because it is the only live control while the composer is disabled.
 *   4. BOUNDED. Two named wall-clock ceilings below.
 *
 * And the exit is always the same: **the answer is sent regardless.** Every
 * non-delivering exit — refused, unreadable, duplicate, nothing recognised,
 * "not on file", timed out, skipped — still sends the turn and attaches an
 * honest note saying the document was not part of it. Silence is not an option
 * this module offers.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THREE THINGS A REVIEWER CAUGHT THAT A HAND TEST WOULD NOT
 * ─────────────────────────────────────────────────────────────────────────
 * (consult-architect, 2026-08-02, `agreement-and-proceed` on this shape)
 *
 * **(a) An id we have never heard of is STILL WAITING, not settled.** The slots
 * are React state and the poll reads a ref mirror updated after render, so for
 * at least one tick the ids being waited on are absent from the map. If absence
 * read as "nothing to wait for", the wait would release instantly and the lane
 * would silently deliver nothing — passing every hand test and failing under
 * load. `slotWaitState` returns `READING` for an unknown id, and
 * `test_unknown_id_is_still_waiting` is the red-test.
 *
 * **(b) Settled is not delivered.** `describeAttachOutcome` returns a non-null
 * `attachState` for `not_on_file`, for `attached_empty`, and for a 200 that
 * recorded nothing. All three END the wait — the buyer answered, and there is
 * nothing more to wait for — and NONE of them put a claim in the store. They
 * take the honest-note path. `slotWaitState` and `slotDelivered` are therefore
 * two separate questions and are deliberately not one function.
 *
 * **(c) A read that succeeds without a confirm can still deliver.** An ordinary
 * document (not a supplier notice) lands on `OUTCOME.READ`, which is the
 * pipeline's "read AND recorded" class — those assertions travel the normal
 * doc-upload path and reach chat without any confirm. So `READ` counts as
 * delivered and asks for no picker. This is what keeps an ordinary attachment
 * from waiting on a card that will never appear.
 */

import { OUTCOME, READ_STATUSES } from './docUploadStatus';
import { ATTACH_OUTCOME } from './noticeAttachStatus';

// ============================================================================
// CEILINGS
// ============================================================================

/**
 * How long the machine phase may take. A scanned PDF goes through a Claude
 * vision OCR pass inside the request, so this is generous on purpose — the
 * ceiling exists to bound a hung socket, not to cut a read short.
 */
export const READ_DEADLINE_MS = 120000;

/**
 * How long we wait for the buyer to answer "who sent this?", measured from the
 * moment the question appears. Long enough to read a card; short enough that a
 * buyer who walked away gets their answer rather than a spinner.
 */
export const CONFIRM_DEADLINE_MS = 120000;

/** Poll interval for the ref mirror. */
export const POLL_INTERVAL_MS = 200;

// ============================================================================
// PER-SLOT STATE
// ============================================================================

export const WAIT = Object.freeze({
  READING: 'reading',        // the machine is still working (or we cannot see it yet)
  NEEDS_CONFIRM: 'confirm',  // read, parked, waiting on the buyer's pick
  SETTLED: 'settled',        // nothing further will change for this slot
});

/**
 * What is this slot still waiting for?
 *
 * `undefined` / `null` slot => `READING`. See note (a) above: an id absent from
 * the mirror is one we have not seen land yet, never one that has finished.
 */
export function slotWaitState(slot) {
  if (!slot) return WAIT.READING;

  const uploadState = slot.uploadState;
  const uploadOutcome = uploadState && uploadState.outcome;

  // Still in flight, or the server says it is still working.
  if (!uploadOutcome || uploadOutcome === OUTCOME.READING) return WAIT.READING;

  // A confirm that has come back — in ANY of its outcomes — ends the wait.
  if (slot.attachState) return WAIT.SETTLED;

  // ── Is a picker on screen for this slot? ──────────────────────────────────
  // This MUST be the same predicate the card uses (`renderUploadCard`'s
  // `noticeNeedsSupplier` + `showPicker`), and it is deliberately derived the
  // same way rather than re-stated loosely. If the wait's idea of "a question
  // is on screen" ever drifts from the card's, the turn would send while a
  // picker sat unanswered — which is Ryan's defect again, in a narrower case.
  //
  // The second branch is Lane 676's SECOND DOOR: re-uploading a letter whose
  // sender was never confirmed short-circuits to `duplicate` before the
  // pipeline runs, and the card still offers the picker because the PRIOR row
  // is parked and attachable. Confirming there writes the claims exactly as a
  // first upload would, so it is just as much worth waiting for.
  const needsSupplier =
    uploadOutcome === OUTCOME.AWAITING_SUPPLIER ||
    (uploadOutcome === OUTCOME.DUPLICATE && uploadState.priorAwaitingSupplier === true);

  if (needsSupplier) {
    // The orphan guard, mirrored from the card: with no candidate id there is
    // no row to attach to, so no picker renders and no confirm can ever
    // arrive. Waiting for one would be waiting forever. Settled (undelivered)
    // — the honest note covers it.
    const candidateId = uploadState.candidateId;
    if (candidateId === null || candidateId === undefined) return WAIT.SETTLED;
    return WAIT.NEEDS_CONFIRM;
  }

  // read / nothing_found / plain duplicate / blocked / failed / unknown are all
  // terminal for the machine and none of them asks a question.
  return WAIT.SETTLED;
}

/**
 * Did this slot actually put something the chat can read into the store?
 *
 * Separate from `slotWaitState` on purpose — see note (b). "The buyer answered"
 * and "there is something to answer with" are different facts and conflating
 * them is how a `not_on_file` confirm would be reported as a success.
 */
export function slotDelivered(slot) {
  if (!slot) return false;

  const uploadOutcome = slot.uploadState && slot.uploadState.outcome;

  // An ordinary document: read AND recorded by the doc-upload pipeline, whose
  // assertions reach chat through the normal allow-listed path. No confirm is
  // involved and none is needed — note (c).
  if (uploadOutcome === OUTCOME.READ) return true;

  // M4C WS5.4 (Lane 1119): a PLAIN duplicate is delivered only when the
  // server explicitly said the prior row is chat-readable AND its stored
  // text is available (`attachment_text_available === true`; a missing key
  // — an older backend — stays undelivered). A duplicate of a PARKED notice
  // (`priorAwaitingSupplier`) keeps falling through to the attach/confirm
  // path below, unchanged.
  if (uploadOutcome === OUTCOME.DUPLICATE && slot.uploadState
      && !slot.uploadState.priorAwaitingSupplier) {
    return (
      READ_STATUSES.includes(slot.uploadState.priorStatus)
      && slot.uploadState.attachmentTextAvailable === true
    );
  }

  const attach = slot.attachState;
  if (!attach) return false;

  // The SAME predicate the upload card uses to decide it may claim something
  // was recorded: an attributed outcome AND a positive recorded count. A
  // `partial` counts — some claims really did land — while `not_on_file`,
  // `attached_empty`, `nothing_recorded` and `unknown` do not.
  const attributed =
    attach.outcome === ATTACH_OUTCOME.ATTACHED ||
    attach.outcome === ATTACH_OUTCOME.PARTIAL;

  return attributed && Number.isInteger(attach.recorded) && attach.recorded > 0;
}

// ============================================================================
// AGGREGATE OVER THE IDS OF ONE SUBMIT
// ============================================================================

/**
 * Roll the per-slot states up over exactly the ids attached to this submit.
 *
 * `byId` may be a Map or a plain object; ids missing from it are `READING` per
 * note (a). Returns the phase the WAIT is in overall plus the slots that are
 * holding it there, so the caller can name the file in the waiting line.
 */
export function aggregateWait(byId, ids) {
  const lookup = (id) => {
    if (!byId) return undefined;
    if (typeof byId.get === 'function') return byId.get(id);
    return byId[id];
  };

  const reading = [];
  const needsConfirm = [];

  (ids || []).forEach((id) => {
    const slot = lookup(id);
    const state = slotWaitState(slot);
    if (state === WAIT.READING) reading.push({ id, slot });
    else if (state === WAIT.NEEDS_CONFIRM) needsConfirm.push({ id, slot });
  });

  // Reading outranks confirming: while any file is still being read, that is
  // the honest thing to report, even if an earlier file is already asking.
  if (reading.length > 0) {
    return { phase: WAIT.READING, reading, needsConfirm, blocking: reading };
  }
  if (needsConfirm.length > 0) {
    return { phase: WAIT.NEEDS_CONFIRM, reading, needsConfirm, blocking: needsConfirm };
  }
  return { phase: WAIT.SETTLED, reading, needsConfirm, blocking: [] };
}

// ============================================================================
// COPY
// ============================================================================
//
// ⚠️ UNSIGNED. Every string below is customer-facing on a LIVE chat surface and
// is awaiting Ryan's sign-off (handover: "OWED TO RYAN — copy sign-off").
//
// The honesty rule they all obey: say what Wingman is doing or what it does not
// have. Never assert what the answer did or did not take into account — the
// client cannot know that, and claiming it would be the same class of error as
// the `UNKNOWN` branch that used to say "Nothing was created".

const fileNameOf = (entry) =>
  (entry && entry.slot && entry.slot.file && entry.slot.file.name) || null;

/** The line that replaces "Analyzing..." while the turn is held. */
export function describeWaiting(aggregate) {
  if (!aggregate || aggregate.phase === WAIT.SETTLED) return null;

  const blocking = aggregate.blocking || [];
  const name = fileNameOf(blocking[0]);
  const more = blocking.length - 1;

  if (aggregate.phase === WAIT.READING) {
    if (name && more > 0) {
      return `Reading ${name} and ${more} more before answering…`;
    }
    return name
      ? `Reading ${name} before answering…`
      : 'Reading your document before answering…';
  }

  // NEEDS_CONFIRM — name the thing the buyer has to do, not just the state.
  if (name && more > 0) {
    return `Waiting for you to confirm who sent ${name} and ${more} more. Then I'll answer.`;
  }
  return name
    ? `Waiting for you to confirm who sent ${name}. Then I'll answer.`
    : "Waiting for you to confirm who sent this document. Then I'll answer.";
}

/** The label on the escape control. */
export const SKIP_LABEL = 'Answer now without the document';

/**
 * The honest note attached to the answer when the document did not make it in.
 *
 * `null` when every attached file delivered — a note in that case would be
 * noise. The wording deliberately reports the DOCUMENT's state, never the
 * answer's: "this answer was written without it" is a claim about what Wingman
 * had, which is knowable, rather than about what the model used, which is not.
 */
export function describeUndelivered(byId, ids, { skipped = false, timedOut = false } = {}) {
  const lookup = (id) => {
    if (!byId) return undefined;
    if (typeof byId.get === 'function') return byId.get(id);
    return byId[id];
  };

  const all = (ids || []).map((id) => ({ id, slot: lookup(id) }));
  if (all.length === 0) return null;

  const missing = all.filter((entry) => !slotDelivered(entry.slot));
  if (missing.length === 0) return null;

  const names = missing.map(fileNameOf).filter(Boolean);
  const subject =
    names.length === 1
      ? names[0]
      : names.length > 1
        ? `${names.length} attached documents`
        : 'your document';

  if (skipped) {
    return `You asked me to answer before ${subject} was ready, so this answer was written without it.`;
  }
  if (timedOut) {
    return `${subject} was not ready in time, so this answer was written without it. Ask again once it is.`;
  }
  // The ordinary case: the read or the confirm produced nothing readable.
  return `Nothing from ${subject} was available to this answer. Check its status above.`;
}

// ============================================================================
// LANE 1400 WS1 — HONEST UPLOADS (CHAT_UPLOAD_HONESTY_ENABLED)
// ============================================================================
//
// With the flag OFF the chat sends only the ids `slotDelivered` vouches for
// (M4C WS5.4, above) and nothing else. With it ON the server is told about
// EVERY file of the Send, so it can say plainly which file it could not read
// instead of the model analyzing a different upload without a word (ruling
// R8). Two lists, both bounded:
//
//   * `turnCandidateIds` — every attached file that has a candidate id,
//     readable or not. The server re-checks each one (tenant, user, status);
//     an id is never trusted for scope.
//   * `turnFailedUploads` — files that never got an id (refused before
//     upload, 413/415, network), as `{file_name, reason_code}`. The server
//     owns the sentence; the browser sends only the code.

/** Mirror of `helpers/part_list_price_analysis.MAX_CANDIDATE_IDS` (the server
 *  answers 422 above it). */
export const MAX_TURN_CANDIDATE_IDS = 10;
/** Mirror of `helpers/chat_upload_honesty.MAX_FAILED_UPLOADS`. */
export const MAX_FAILED_UPLOADS = 10;
/** Mirror of `helpers/chat_upload_honesty.MAX_FILE_NAME_CHARS`. */
export const MAX_FAILED_UPLOAD_NAME_CHARS = 200;

const lookupSlot = (byId, id) => {
  if (!byId) return undefined;
  if (typeof byId.get === 'function') return byId.get(id);
  return byId[id];
};

/** Every attached file of this turn that has a candidate id (newest last,
 *  at most MAX_TURN_CANDIDATE_IDS, each id once). */
export function turnCandidateIds(byId, ids) {
  const out = [];
  (ids || []).forEach((id) => {
    const candidateId = lookupSlot(byId, id)?.uploadState?.candidateId;
    if (Number.isInteger(candidateId) && candidateId > 0 && !out.includes(candidateId)) {
      out.push(candidateId);
    }
  });
  return out.slice(-MAX_TURN_CANDIDATE_IDS);
}

/** Files of this turn that failed before getting a candidate id. */
export function turnFailedUploads(byId, ids) {
  const out = [];
  (ids || []).forEach((id) => {
    const slot = lookupSlot(byId, id);
    const state = slot && slot.uploadState;
    if (!state || state.candidateId != null) return;
    if (state.outcome !== OUTCOME.BLOCKED && state.outcome !== OUTCOME.FAILED) return;
    const rawName = state.fileName || (slot.file && slot.file.name) || '';
    const fileName = String(rawName).replace(/\s+/g, ' ').trim()
      .slice(0, MAX_FAILED_UPLOAD_NAME_CHARS);
    if (!fileName) return;
    out.push({ file_name: fileName, reason_code: state.failureCode || 'upload_failed' });
  });
  return out.slice(-MAX_FAILED_UPLOADS);
}
